/**
 * Colour theme + text size for the web app — the SAME theme list as the
 * terminal (@quantum/tokens/themes, owner 27.09.2026: one look for both).
 * `data-theme` picks the tokens.css base (dark | light); the chosen theme
 * paints its palette as inline CSS vars on <html>. glasswork.js re-reads
 * them (its MutationObserver watches data-theme; step 10 adds `style`).
 */
import { THEMES, DEFAULT_THEME, themeDef, themeVars } from '@quantum/tokens/themes';

const KEY = 'quantum.theme';
const SCALE_KEY = 'quantum.textScale';
const VALID = new Set(THEMES.map((t) => t.id));
let applied = new Set();

export function getTheme() {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'dark') return DEFAULT_THEME; // pre-27.09 value — Graphite replaces it
    if (v && VALID.has(v)) return v;
  } catch { /* private mode */ }
  return DEFAULT_THEME;
}

export function getTextScale() {
  try {
    const n = Number(localStorage.getItem(SCALE_KEY));
    if (n >= 90 && n <= 130) return n;
  } catch { /* private mode */ }
  return 100;
}

function paint() {
  const root = document.documentElement;
  const t = getTheme();
  root.setAttribute('data-theme', themeDef(t).base);
  const vars = themeVars(t);
  for (const k of applied) if (!(k in vars)) root.style.removeProperty(k);
  for (const [k, v] of Object.entries(vars)) root.style.setProperty(k, v);
  applied = new Set(Object.keys(vars));
  root.style.setProperty('--fs-scale', String(getTextScale() / 100));
}

export function applyTheme(t) {
  try { localStorage.setItem(KEY, t); } catch { /* private mode */ }
  paint();
}

export function setTextScale(pct) {
  try { localStorage.setItem(SCALE_KEY, String(pct)); } catch { /* private mode */ }
  paint();
}

export function toggleTheme() {
  const next = getTheme() === 'light' ? DEFAULT_THEME : 'light';
  applyTheme(next);
  return next;
}

/** Call once at boot, before React renders, to avoid a flash of the wrong theme. */
export function initTheme() {
  paint();
}
